import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, IndianRupee, Loader2, Building2, Link2, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { PageLoader } from "@/components/common/Loading";

const inputCls = "h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [apiForm, setApiForm] = useState({ google: { clientId: "", clientSecret: "", redirectUri: "https://gmb.awebbee.com/api/google/callback" }, ai: { provider: "openrouter", openRouterApiKey: "", model: "openrouter/free", openaiApiKey: "", geminiApiKey: "" } });
  const [saving, setSaving] = useState(false);
  const [savingApi, setSavingApi] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/clients/${id}`);
    setData(data);
    const c = data.client;
    setForm({ businessName: c.businessName, ownerName: c.ownerName, phone: c.phone, category: c.category, city: c.city, plan: c.plan, status: c.status, monthlyPrice: c.monthlyPrice, subscriptionEnd: c.subscriptionEnd ? c.subscriptionEnd.slice(0, 10) : "", aiPostsPerDay: c.limits?.aiPostsPerDay || 2 });
    const savedApi = data.settings?.apiKeys || {};
    setApiForm({
      google: { clientId: "", clientSecret: "", redirectUri: "https://gmb.awebbee.com/api/google/callback", ...(savedApi.google || {}) },
      ai: { provider: "openrouter", openRouterApiKey: "", model: "openrouter/free", openaiApiKey: "", geminiApiKey: "", ...(savedApi.ai || {}) },
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setApi = (section, key, value) => setApiForm((f) => ({ ...f, [section]: { ...(f[section] || {}), [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.aiPostsPerDay !== undefined) {
        payload.limits = { ...(data.client.limits || {}), aiPostsPerDay: payload.aiPostsPerDay };
        delete payload.aiPostsPerDay;
      }
      await api.put(`/admin/clients/${id}`, payload);
      toast.success("Client updated");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const saveApiKeys = async () => {
    setSavingApi(true);
    try {
      await api.put(`/settings?clientId=${id}`, { clientId: id, apiKeys: apiForm });
      toast.success("Client API keys saved");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSavingApi(false); }
  };

  const markPayment = async (status) => {
    try {
      await api.post(`/billing/manual-payment`, { clientId: id, status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return <PageLoader />;
  const { client, team: teamRaw, subscription, google, stats } = data;
  const team = arr(teamRaw);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/admin/clients")} className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline"><ArrowLeft className="h-4 w-4" /> Back to clients</button>

      <div className="card-surface p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700"><Building2 className="h-6 w-6" /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">{client.businessName}</h1>
              <p className="text-sm text-slate-500">{client.email} · {client.username}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={client.status} />
            <StatusBadge status={client.paymentStatus} />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Posts" value={stats.posts} />
          <Stat label="Reviews" value={stats.reviews} />
          <Stat label="Review Requests" value={stats.requests} />
          <Stat label="Google" value={google?.connected ? "Connected" : "Not connected"} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2 card-surface p-6">
          <h2 className="font-heading text-lg font-semibold text-slate-800">Edit Client</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Inp label="Business Name" v={form.businessName} on={(v) => set("businessName", v)} />
            <Inp label="Owner Name" v={form.ownerName} on={(v) => set("ownerName", v)} />
            <Inp label="Phone" v={form.phone} on={(v) => set("phone", v)} />
            <Inp label="Category" v={form.category} on={(v) => set("category", v)} />
            <Inp label="City" v={form.city} on={(v) => set("city", v)} />
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Plan</label>
              <Select value={form.plan} onValueChange={(v) => set("plan", v)}>
                <SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{["Starter", "Business", "Pro", "Agency"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Status</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{["trial", "active", "suspended", "expired"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Inp label="Monthly Price (₹)" type="number" v={form.monthlyPrice} on={(v) => set("monthlyPrice", Number(v))} />
            <Inp label="Subscription Expiry" type="date" v={form.subscriptionEnd} on={(v) => set("subscriptionEnd", v)} />
            <Inp label="Daily AI Posts Limit" type="number" v={form.aiPostsPerDay} on={(v) => set("aiPostsPerDay", Number(v))} />
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-client-btn" className="rounded-xl bg-brand-700 text-white hover:bg-brand-800">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save changes</>}
          </Button>
        </div>

        <div className="space-y-5">
          <div className="card-surface p-6">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Billing</h2>
            <p className="mt-2 text-sm text-slate-500">Payment status</p>
            <div className="mt-1"><StatusBadge status={client.paymentStatus} /></div>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => markPayment("paid")} className="flex-1 rounded-xl bg-green-600 text-white hover:bg-green-700" data-testid="mark-paid-btn"><IndianRupee className="mr-1 h-4 w-4" /> Mark Paid</Button>
              <Button onClick={() => markPayment("unpaid")} variant="outline" className="flex-1 rounded-xl">Mark Unpaid</Button>
            </div>
            <p className="mt-4 text-xs text-slate-400">Invoices</p>
            <div className="mt-1 space-y-1">
              {(subscription?.history || []).map((h, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{new Date(h.date).toLocaleDateString()}</span>
                  <span className="font-semibold">₹{h.amount}</span>
                  <StatusBadge status={h.status} />
                </div>
              ))}
              {(!subscription?.history || subscription.history.length === 0) && <p className="text-sm text-slate-400">No invoices yet</p>}
            </div>
          </div>

          <div className="card-surface p-6">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Team ({team.length})</h2>
            <div className="mt-3 space-y-2">
              {team.map((u) => (
                <div key={u.id} className="flex items-center justify-between text-sm">
                  <div><p className="font-medium text-slate-700">{u.name}</p><p className="text-xs text-slate-400">{u.username}</p></div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize text-slate-600">{u.role.replace("_", " ")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-800">Client API Connections</h2>
            <p className="mt-1 text-sm text-slate-500">Set Google OAuth and AI keys for this client from Super Admin. These override global settings for this client only.</p>
          </div>
          <Button onClick={saveApiKeys} disabled={savingApi} className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" data-testid="save-client-api-keys-btn">
            {savingApi ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save API Keys</>}
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-5">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Link2 className="h-5 w-5 text-brand-600" /> Google OAuth / GMB API</h3>
            <div className="space-y-3">
              <ApiInp label="GOOGLE_CLIENT_ID" v={apiForm.google?.clientId} on={(v) => setApi("google", "clientId", v)} />
              <ApiInp label="GOOGLE_CLIENT_SECRET" type="password" v={apiForm.google?.clientSecret} on={(v) => setApi("google", "clientSecret", v)} />
              <ApiInp label="GOOGLE_REDIRECT_URI" v={apiForm.google?.redirectUri} on={(v) => setApi("google", "redirectUri", v)} />
            </div>
            <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700">Add the same redirect URI in Google Cloud Console OAuth client.</p>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-5">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Bot className="h-5 w-5 text-brand-600" /> AI Provider / OpenRouter</h3>
            <div className="space-y-3">
              <ApiInp label="OPENROUTER_API_KEY" type="password" v={apiForm.ai?.openRouterApiKey} on={(v) => setApi("ai", "openRouterApiKey", v)} />
              <ApiInp label="AI_MODEL" v={apiForm.ai?.model} on={(v) => setApi("ai", "model", v)} placeholder="openrouter/free" />
              <ApiInp label="OPENAI_API_KEY optional" type="password" v={apiForm.ai?.openaiApiKey} on={(v) => setApi("ai", "openaiApiKey", v)} />
              <ApiInp label="GEMINI_API_KEY optional" type="password" v={apiForm.ai?.geminiApiKey} on={(v) => setApi("ai", "geminiApiKey", v)} />
            </div>
            <p className="mt-3 rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-700">For free OpenRouter routing, use AI_MODEL=openrouter/free.</p>
          </div>
        </div>
      </div>

    </div>
  );
}

function Stat({ label, value }) {
  return <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-400">{label}</p><p className="mt-1 text-lg font-bold text-slate-800">{value}</p></div>;
}
function Inp({ label, v, on, type = "text" }) {
  return <div><label className="mb-1 block text-xs font-medium text-slate-600">{label}</label><input type={type} className={inputCls} value={v ?? ""} onChange={(e) => on(e.target.value)} /></div>;
}
function ApiInp({ label, v, on, type = "text", placeholder = "" }) {
  return <div><label className="mb-1 block text-xs font-medium text-slate-600">{label}</label><input type={type} placeholder={placeholder} className={inputCls} value={v ?? ""} onChange={(e) => on(e.target.value)} /></div>;
}
